import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { API, inr } from "../lib/api";

export const STATUS_STEPS = ["placed", "confirmed", "shipped", "out_for_delivery", "delivered"];

export function Timeline({ status, timeline }) {
  const cancelled = ["cancelled", "return_requested", "returned", "refunded"].includes(status);
  const idx = STATUS_STEPS.indexOf(status);
  if (cancelled)
    return <p className="text-red-400 text-sm font-bold uppercase tracking-widest" data-testid="order-cancelled-label">{status.replace(/_/g, " ")}</p>;
  return (
    <div className="flex items-center gap-0 overflow-x-auto py-2" data-testid="order-timeline">
      {STATUS_STEPS.map((s, i) => (
        <div key={s} className="flex items-center shrink-0">
          <div className="flex flex-col items-center">
            <div className={`h-3 w-3 rotate-45 border ${i <= idx ? "bg-gold border-gold" : "border-[#262626]"}`} />
            <span className={`text-[9px] uppercase tracking-wider mt-2 ${i <= idx ? "text-gold" : "text-muted-foreground"}`}>
              {s.replace(/_/g, " ")}
            </span>
          </div>
          {i < STATUS_STEPS.length - 1 && <div className={`h-px w-8 sm:w-14 mx-1 mb-4 ${i < idx ? "bg-gold" : "bg-[#262626]"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    API.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-20" data-testid="order-success-page">
      <CheckCircle2 size={56} className="text-gold mb-6" />
      <h1 className="font-display font-black text-4xl tracking-tighter">Order locked in.</h1>
      <p className="text-muted-foreground mt-3">
        {order ? <>Order <span className="text-gold font-bold" data-testid="order-number">{order.order_number}</span> is confirmed.
          A confirmation email is on its way to {order.email}.</> : "Loading order details..."}
      </p>
      {order && (
        <div className="border border-[#262626] bg-ink-surface p-6 mt-10">
          <Timeline status={order.status} timeline={order.timeline} />
          <div className="space-y-3 mt-6">
            {order.items.map((i) => (
              <div key={i.sku + i.size} className="flex items-center gap-3 text-sm">
                <img src={i.image} alt="" className="h-14 w-11 object-cover" loading="lazy" />
                <div className="flex-1">
                  <p className="font-bold">{i.name}</p>
                  <p className="text-xs text-muted-foreground">{i.size}/{i.color} × {i.qty}</p>
                </div>
                <p className="text-gold font-bold">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-display font-black text-lg border-t border-[#262626] mt-5 pt-4">
            <span>Total</span><span className="gold-text">{inr(order.total)}</span>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/invoice`}
              data-testid="download-invoice-btn"
              className="inline-flex items-center gap-2 border border-gold text-gold px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-gold/10 transition-colors">
              <Download size={14} /> Invoice PDF
            </a>
            <Link to="/shop" className="btn-gold inline-flex items-center px-5 py-3 text-xs font-bold uppercase tracking-wider" data-testid="continue-shopping-btn">
              Continue shopping
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
